import { useTranslation } from "react-i18next";
import { MAX_EXPECTED_STUDENTS } from "@binogi-livequiz/shared";

// Digits the input accepts: enough for the cap, so typing past it clamps.
const MAX_DIGITS = String(MAX_EXPECTED_STUDENTS).length;

export function StudentCountInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (n: number) => void;
}) {
  const { t } = useTranslation();
  const display = value > 0 ? String(value) : "";
  const has = value > 0;
  // When unset the input shows a "20" placeholder. Treat that as the
  // starting point so + jumps to 21 (not 1) and − jumps to 19 — matches
  // what the teacher visually expects to happen.
  const PLACEHOLDER = 20;
  const inc = () =>
    onChange(value > 0 ? Math.min(MAX_EXPECTED_STUDENTS, value + 1) : PLACEHOLDER + 1);
  const dec = () =>
    onChange(value > 0 ? Math.max(1, value - 1) : PLACEHOLDER - 1);
  return (
    <div className="text-center">
      <label
        htmlFor="studentCount"
        className="block text-sm font-semibold mb-3 text-white/80"
      >
        {t("host.number_of_students")}
        <span
          className={`ml-1 ${has ? "text-white/30" : "text-brand-amber"}`}
        >
          *
        </span>
      </label>
      <div className="inline-flex items-stretch gap-3">
        <button
          type="button"
          onClick={dec}
          disabled={value === 1}
          aria-label={t("host.fewer_students")}
          className="w-14 rounded-2xl bg-brand-ink2 text-3xl font-bold text-white/70 hover:text-white hover:bg-brand-ink3 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          −
        </button>
        <input
          id="studentCount"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={display}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^0-9]/g, "").slice(0, MAX_DIGITS);
            const n = raw === "" ? 0 : Math.min(MAX_EXPECTED_STUDENTS, parseInt(raw, 10));
            onChange(n);
          }}
          placeholder="20"
          className="w-40 bg-brand-ink2 text-center text-5xl md:text-6xl font-extrabold tabular-nums py-4 rounded-2xl outline-none focus:ring-2 focus:ring-brand-green placeholder:text-white/20 placeholder:font-bold"
        />
        <button
          type="button"
          onClick={inc}
          aria-label={t("host.more_students")}
          className="w-14 rounded-2xl bg-brand-ink2 text-3xl font-bold text-white/70 hover:text-white hover:bg-brand-ink3 transition-colors"
        >
          +
        </button>
      </div>
    </div>
  );
}
